/**
 * /mail/operator/suppressions - operator-internal mail suppression list (OCP-206).
 *
 * Shows OMS's `oms_operator_suppressions` rows (recipients we've stopped
 * sending to because of hard-bounce / complaint / manual entry).
 * Operator can clear an address to re-enable sends — clears upstream
 * in OMS AND in OCP's local mail_suppressions mirror in one round-trip.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { useTransientState } from '../lib/useTransientState';
import { fmtDateTime } from '../lib/format-time';

interface SuppressionRow {
  id: string;
  email: string;
  reason: 'hard-bounce' | 'complaint' | 'manual';
  added_at: string;
  added_by: string | null;
}

interface SuppressionListResponse {
  suppressions: SuppressionRow[];
  nextCursor: string | null;
}

const REASON_FILTERS = ['', 'hard-bounce', 'complaint', 'manual'] as const;

export function AdminMailOperatorSuppressionsPage() {
  const [items, setItems] = useState<SuppressionRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clearingAddress, setClearingAddress] = useState<string | null>(null);
  const [flash, setFlash] = useTransientState<string | null>(null, 5000);

  const load = useCallback(
    async (mode: 'replace' | 'append') => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('limit', '50');
        if (reasonFilter) params.set('reason', reasonFilter);
        if (mode === 'append' && nextCursor) params.set('cursor', nextCursor);
        const r = await api<SuppressionListResponse>(
          `/v1/admin/mail/operator/suppressions?${params}`,
        );
        if (mode === 'append') setItems((prev) => [...prev, ...r.suppressions]);
        else setItems(r.suppressions);
        setNextCursor(r.nextCursor);
      } catch (err) {
        if (err instanceof ApiError) setError(`Could not load suppressions (HTTP ${err.status}).`);
        else setError('Could not load suppressions.');
      } finally {
        setLoading(false);
      }
    },
    [reasonFilter, nextCursor],
  );

  useEffect(() => {
    void load('replace');
  }, [reasonFilter]);

  const handleClear = useCallback(
    async (address: string) => {
      setClearingAddress(address);
      setError(null);
      try {
        await api(`/v1/admin/mail/operator/suppressions/${encodeURIComponent(address)}`, {
          method: 'DELETE',
        });
        setItems((prev) => prev.filter((i) => i.email !== address));
        setFlash(`Cleared ${address}. Future sends to this address will go through.`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          // Already gone in OMS — still drop locally
          setItems((prev) => prev.filter((i) => i.email !== address));
          setFlash(`${address} was already cleared upstream.`);
        } else if (err instanceof ApiError) {
          setError(`Could not clear ${address} (HTTP ${err.status}).`);
        } else {
          setError(`Could not clear ${address}.`);
        }
      } finally {
        setClearingAddress(null);
      }
    },
    [],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Operator suppressions</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Recipients OCP has stopped mailing because of a hard bounce, a complaint, or a
          manual entry. Clearing an address re-enables sends.
        </p>
      </div>

      {flash && (
        <div
          role="status"
          className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300"
        >
          {flash}
        </div>
      )}

      <div className="flex items-baseline gap-3">
        <label htmlFor="operator-suppressions-reason" className="text-xs text-gray-500">
          Reason:
        </label>
        <select
          id="operator-suppressions-reason"
          data-testid="operator-suppressions-reason-filter"
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs"
        >
          {REASON_FILTERS.map((r) => (
            <option key={r || 'all'} value={r}>
              {r || 'All'}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && items.length === 0 && (
        <p className="text-sm text-gray-500">Loading...</p>
      )}

      {!loading && items.length === 0 && !error && (
        <p className="text-sm text-gray-500">No operator suppressions on the list.</p>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2">Address</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Added</th>
                <th className="px-3 py-2">Added by</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((row) => (
                <tr key={row.id} data-testid={`operator-suppression-row-${row.id}`}>
                  <td className="px-3 py-2 text-xs">{row.email}</td>
                  <td className="px-3 py-2 text-xs">
                    <ReasonBadge reason={row.reason} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                    {formatTimestamp(row.added_at)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{row.added_by ?? '-'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`operator-suppression-clear-${row.id}`}
                      onClick={() => void handleClear(row.email)}
                      disabled={clearingAddress === row.email}
                      className="rounded border border-gray-300 dark:border-gray-700 px-2 py-0.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
                    >
                      {clearingAddress === row.email ? 'Clearing...' : 'Clear'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && (
        <div className="flex justify-center">
          <button
            type="button"
            data-testid="operator-suppressions-load-more"
            disabled={loading}
            onClick={() => void load('append')}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

function ReasonBadge({ reason }: { reason: SuppressionRow['reason'] }) {
  const cls =
    reason === 'hard-bounce' ? 'bg-red-100 text-red-800 dark:text-red-300'
      : reason === 'complaint' ? 'bg-amber-100 text-amber-900 dark:text-amber-200'
      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  return <span className={`rounded px-1.5 py-0.5 ${cls}`}>{reason}</span>;
}

function formatTimestamp(iso: string): string {
  try {
    return fmtDateTime(iso);
  } catch {
    return iso;
  }
}
